"use client";

import { useDirection } from "@radix-ui/react-direction";
import * as React from "react";
import { usePathname } from "next/navigation";
import { BibliotekaIcon, PieChartIcon, UsersIcon } from "@/components/icons";
import { useTranslations } from "next-intl";
import { useAppSelector } from "@/store";
import { USER_ROLES, UserRole } from "@/src/lib/auth";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { NavUser } from "./NavUser";
import { NavMain } from "./NavMain";
import { NavExtras } from "./NavExtras";
import { Logo } from "./Logo";

type NavConfigItem = {
  key: string;
  titleKey: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  roles: ReadonlyArray<UserRole>;
};

const MANAGER_ROLES = [
  USER_ROLES.TEACHER,
  USER_ROLES.REVIEWER,
  USER_ROLES.MANAGER,
  USER_ROLES.ADMIN,
] as const;

const NAV_CONFIG: ReadonlyArray<NavConfigItem> = [
  {
    key: "assignments",
    titleKey: "label-assignments",
    icon: BibliotekaIcon,
    href: "/assignments",
    roles: [
      USER_ROLES.STUDENT,
      USER_ROLES.PROCTOR,
      ...MANAGER_ROLES,
    ],
  },
  {
    key: "users",
    titleKey: "label-users",
    icon: UsersIcon,
    href: "/groups",
    roles: MANAGER_ROLES,
  },
  {
    key: "analytics",
    titleKey: "label-analytics",
    icon: PieChartIcon,
    href: "/analytics",
    roles:  [
      USER_ROLES.PROCTOR,
      ...MANAGER_ROLES,
    ],
  },
];

function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const t = useTranslations();
  const pathname = usePathname();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user } = useAppSelector((s) => s.auth);

  const role = user?.group as UserRole | undefined;

  const sections = NAV_CONFIG
    .filter((item) => role && item.roles.includes(role))
    .map((item) => ({
      title: t(item.titleKey),
      icon: item.icon,
      href: item.href,
      isActive: pathname.startsWith(item.href),
      is_coming_soon: false,
    }));

  const direction = useDirection();


  return (
    <Sidebar
      className="data-[collapsible=icon]:w-16 !min-w-16"
      collapsible="icon"
      side={direction === "rtl" ? "right" : "left"}
      {...props}
    >
      <SidebarHeader className="border-b-2 border-[rgba(255,255,255,0.75)] h-16 flex items-start justify-center px-4">
        <Logo variant="dark" collapsed={isCollapsed} />
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={sections} />
      </SidebarContent>

      <Separator className="my-2 opacity-50" />
      <NavExtras />
      <SidebarFooter className="px-2 pb-3">
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
export default AppSidebar;
